import { ServerError } from '@apollo/client';
import type { InferGetServerSidePropsType, NextPage } from 'next';
import Head from 'next/head';
import { generateNextSeo } from 'next-seo/pages';

import { Author } from '#components/Author/Author';
import { getAuthorTwitterHandle } from '#components/Author/Author.utils';
import { AuthorArticlesResultsTable } from '#components/Author/AuthorArticlesResultsTable';
import { AuthorHeader } from '#components/Author/AuthorHeader';
import { AuthorDocument } from '#components/Author/__generated__/Author.generated';
import { SchemaOrg } from '#components/SchemaOrg';
import { buildAuthorName } from '#cutils/builders/buildAuthorName';
import { buildAuthorProfileLink } from '#cutils/builders/buildAuthorProfileLink';
import { notNullOrUndefined } from '#cutils/misc/notNullOrUndefined';
import type { CacheTag } from '#cutils/page-context/edge-cache-tags';
import { createDefaultGetServerSideProps } from '#cutils/page-context/server-side-props';
import { buildAuthorSeoConfig } from '#cutils/seo/buildAuthorSeoConfig';
import { buildAuthorStructuredData } from '#cutils/seo/schema-org/buildAuthorStructuredData';
import { envConfigServer } from '#sconfig/env.server';
import { throwOnGraphQLSemanticErrorForPaths } from '#sutils/graphql-error-matcher';
import { logger } from '#utils/logging/logger';
import { extractSlugAndIdFromPathSegment } from '#utils/pathUtils';
import { PageView } from 'react-tracktor';

type Props = InferGetServerSidePropsType<typeof getServerSideProps>;

const AuthorPage: NextPage<Props> = ({ isPreviewRequest, author, authorName, seoConfig, authorArticlesSchema, authorSchema }: Props) => {
	return (
		<>
			<Head>{generateNextSeo(seoConfig)}</Head>

			{/* Injects script tags with structured data. */}

			<SchemaOrg schema={authorSchema} />
			<SchemaOrg schema={authorArticlesSchema} />

			<PageView
				key={authorName}
				pageViewData={{
					data: author.trackingInfos,
					event: { page: author.shareUrl, type: 'PageViewEvent' },
				}}
			/>

			<Author>
				<AuthorHeader
					description={author.description}
					jobTitle={author.jobTitle}
					name={authorName}
					profilePicture={author.profilePicture}
					socialMediaAccounts={author.socialMediaAccounts}
				/>

				<AuthorArticlesResultsTable authorName={authorName} authorGuid={author.guid} isPreviewRequest={isPreviewRequest} />
			</Author>
		</>
	);
};

const AUTHOR_PATH = ['author'];

export const getServerSideProps = createDefaultGetServerSideProps(async (ctx, apolloClient, withCacheTag) => {
	const { slug: authorSlug, id: authorGuid } = extractSlugAndIdFromPathSegment(ctx.params?.author);

	if (!authorGuid) {
		return { notFound: true };
	}

	try {
		const { data, error } = await apolloClient.query({
			query: AuthorDocument,
			variables: { authorGuid: authorGuid, count: 10, withCacheTag },
			// this is a dynamic query on the authors page
			fetchPolicy: 'cache-first',
		});

		throwOnGraphQLSemanticErrorForPaths([AUTHOR_PATH], error);
		if (ServerError.is(error)) {
			throw error;
		}

		if (data?.author == null) {
			logger.warn(`Author with id '%s' not found`, authorGuid);
			return { notFound: true };
		}

		// if the name of the author changed, redirect to the correct url
		if (`/${authorSlug ?? ''},${authorGuid}` !== `${data.author.slug},${authorGuid}`) {
			const { search } = new URL(ctx.resolvedUrl, 'http://localhost');

			return {
				redirect: {
					destination: `/autoren${data.author.slug},${authorGuid}${search}`,
					permanent: true,
				},
			};
		}

		// if the author is inactive return not found
		if (!data.author.isActive) {
			return { notFound: true };
		}

		const authorName = buildAuthorName(data.author);
		const navigationTitle = authorName || 'Autoren';
		const authorProfileLink = buildAuthorProfileLink(data.author);
		const authorTwitterHandle = getAuthorTwitterHandle(data.author);

		const cacheTags = new Set<CacheTag>();
		if (withCacheTag) {
			for (const tag of data.author.withCache ?? []) {
				if (tag) {
					cacheTags.add(tag as CacheTag);
				}
			}
			for (const { rowId } of data.authorArticles?.edges
				?.filter(notNullOrUndefined)
				.map(({ node }) => node)
				.filter(notNullOrUndefined) ?? []) {
				cacheTags.add(`a.${rowId}`);
			}
		}

		const appUrl = `${envConfigServer.SELF_ORIGIN}${envConfigServer.BASE_URL}`;

		const seoConfig = buildAuthorSeoConfig(
			{
				authorName,
				description: data.author.jobTitle,
				handle: authorTwitterHandle,
				path: authorProfileLink,
				profilePicture: data.author.profilePicture ?? undefined,
			},
			appUrl
		);

		const structuredData = buildAuthorStructuredData(data.author, data.authorArticles, appUrl);

		return {
			props: {
				author: data.author,
				authorName,
				seoConfig,
				navigationTitle,
				cacheTags,
				...structuredData,
			},
		};
	} catch (error: any) {
		logger.error(error, `Error during author resolution`);
		throw error;
	}
});

export default AuthorPage;
